function isSupabasePublicStorageUrl(url: string) {
  // Example:
  // https://<project>.supabase.co/storage/v1/object/public/<bucket>/<path>
  return /\/storage\/v1\/object\/public\//.test(url)
}

function hashToHue(input: string) {
  // Deterministic hue in [0, 359] for stable placeholders.
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h) % 360
}

function svgDataUrl(svg: string) {
  // Keep it readable (utf8) and safe for URL contexts.
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

export function getPlacePhotoPlaceholder(params: { placeName: string; variant?: number }) {
  const name = params.placeName.trim() || 'Dolny Śląsk'
  const variant = params.variant ?? 0
  const hue = (hashToHue(name) + variant * 29) % 360
  const hue2 = (hue + 38) % 360
  const hue3 = (hue + 72) % 360

  const initials = name
    .split(/\s+/g)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2)

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="1000" viewBox="0 0 1600 1000" role="img" aria-label="${name}">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="hsl(${hue} 78% 55%)"/>
      <stop offset="0.55" stop-color="hsl(${hue2} 82% 50%)"/>
      <stop offset="1" stop-color="hsl(${hue3} 88% 46%)"/>
    </linearGradient>
    <radialGradient id="r" cx="20%" cy="20%" r="80%">
      <stop offset="0" stop-color="rgba(255,255,255,0.30)"/>
      <stop offset="1" stop-color="rgba(255,255,255,0)"/>
    </radialGradient>
    <filter id="grain">
      <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" stitchTiles="stitch"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 .12 0"/>
    </filter>
  </defs>
  <rect width="1600" height="1000" fill="url(#g)"/>
  <rect width="1600" height="1000" fill="url(#r)"/>
  <rect width="1600" height="1000" filter="url(#grain)"/>
  <g opacity="0.9">
    <circle cx="1260" cy="220" r="260" fill="rgba(255,255,255,0.12)"/>
    <circle cx="360" cy="780" r="340" fill="rgba(0,0,0,0.10)"/>
  </g>
  <g font-family="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial" fill="rgba(255,255,255,0.92)">
    <text x="96" y="160" font-size="44" font-weight="800" letter-spacing="0.5">${name}</text>
    <text x="96" y="230" font-size="20" font-weight="700" opacity="0.78">Paszport Dolnego Śląska</text>
    <text x="96" y="900" font-size="220" font-weight="900" opacity="0.12">${initials || 'PDS'}</text>
  </g>
</svg>`

  return svgDataUrl(svg.trim())
}

function buildSupabaseRenderUrl(params: {
  publicObjectUrl: string
  width: number
  height?: number
  quality?: number
}) {
  // Supabase supports image transformations via:
  // /storage/v1/render/image/public/<bucket>/<path>?width=...&height=...&quality=...
  // We intentionally keep it simple and only do it when URL matches Supabase public storage.
  const u = new URL(params.publicObjectUrl)
  const idx = u.pathname.indexOf('/storage/v1/object/public/')
  if (idx === -1) return params.publicObjectUrl
  const tail = u.pathname.slice(idx + '/storage/v1/object/public/'.length)

  u.pathname = `/storage/v1/render/image/public/${tail}`
  u.searchParams.set('width', String(params.width))
  if (params.height) u.searchParams.set('height', String(params.height))
  if (params.quality) u.searchParams.set('quality', String(params.quality))
  return u.toString()
}

export function getResponsiveImageProps(input: {
  src: string
  widths: number[]
  sizes: string
  quality?: number
  /**
   * Domyślnie true: dla URL-i `.../storage/v1/object/public/...` budujemy `.../render/image/public/...`
   * (srcset + width). Wymaga włączonych transformacji obrazów w projekcie Supabase.
   * Ustaw false, gdy podgląd w `<img>` się nie ładuje, a ten sam plik działa po wejściu w bezpośredni link object/public.
   */
  useSupabaseImageRender?: boolean
}) {
  const raw = input.src.trim()
  const widths = Array.from(new Set(input.widths)).filter((w) => w > 0).sort((a, b) => a - b)
  const sizes = input.sizes
  const useRender = input.useSupabaseImageRender !== false

  // Prefer a mid-size default src (browser can still pick via srcset).
  const fallbackWidth = widths[Math.floor(widths.length / 2)] ?? widths[0] ?? 800

  if (isSupabasePublicStorageUrl(raw) && useRender) {
    const src = buildSupabaseRenderUrl({
      publicObjectUrl: raw,
      width: fallbackWidth,
      quality: input.quality ?? 80,
    })
    const srcSet = widths
      .map(
        (w) =>
          `${buildSupabaseRenderUrl({ publicObjectUrl: raw, width: w, quality: input.quality ?? 80 })} ${w}w`,
      )
      .join(', ')
    return { src, srcSet, sizes }
  }

  return { src: raw, srcSet: undefined, sizes }
}

